import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useUser } from "@supabase/auth-helpers-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Award, Download, CheckCircle, Lock, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { CertificatePreview } from "./CertificatePreview";

interface CertificateRequirement {
  category: string;
  min_questions: number;
  min_accuracy: number;
  estimated_hours: number;
  certificate_title: string;
}

interface UserCertificate {
  id: string;
  category: string;
  certificate_number: string;
  issued_at: string;
  total_questions_answered: number;
  accuracy_percentage: number;
}

interface CategoryProgress {
  category: string;
  title: string;
  totalAttempts: number;
  accuracy: number;
  requiredQuestions: number;
  requiredAccuracy: number;
  estimatedHours: number;
  isEligible: boolean;
  isIssued: boolean;
  certificate?: UserCertificate;
}

const categoryLabels: Record<string, string> = {
  power_bi: 'Power BI',
  sql: 'SQL',
  python: 'Python',
};

export function CertificatesTab() {
  const user = useUser();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState<string | null>(null);
  const [progress, setProgress] = useState<CategoryProgress[]>([]);
  const [selectedCertificate, setSelectedCertificate] = useState<{
    certificate: UserCertificate;
    userName: string;
    title: string;
    hours: number;
  } | null>(null);

  useEffect(() => {
    if (user?.id) {
      fetchProgress();
    }
  }, [user?.id]);

  const fetchProgress = async () => {
    if (!user?.id) return;
    
    setLoading(true);
    try {
      // Fetch requirements
      const { data: requirements } = await supabase
        .from('certificate_requirements')
        .select('*')
        .eq('is_active', true);

      // Fetch user certificates
      const { data: certificates } = await supabase
        .from('user_certificates')
        .select('*')
        .eq('user_id', user.id);

      // Fetch user stats per category
      const { data: rankings } = await supabase
        .from('topic_rankings')
        .select('*')
        .eq('user_id', user.id);

      const progressData: CategoryProgress[] = (requirements || []).map((req: CertificateRequirement) => {
        const stats = rankings?.find(r => r.category === req.category);
        const cert = certificates?.find(c => c.category === req.category);
        
        const totalAttempts = stats?.total_attempts || 0;
        const accuracy = Number(stats?.accuracy_percentage || 0);
        
        return {
          category: req.category,
          title: req.certificate_title,
          totalAttempts,
          accuracy,
          requiredQuestions: req.min_questions,
          requiredAccuracy: Number(req.min_accuracy),
          estimatedHours: req.estimated_hours,
          isEligible: totalAttempts >= req.min_questions && accuracy >= req.min_accuracy,
          isIssued: !!cert,
          certificate: cert,
        };
      });

      setProgress(progressData);
    } catch (error) {
      console.error('Error fetching progress:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateCertificate = async (category: string) => {
    if (!user?.id) return;
    
    setGenerating(category);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      const response = await supabase.functions.invoke('generate-certificate', {
        body: { category },
        headers: {
          Authorization: `Bearer ${session?.access_token}`,
        },
      });

      if (response.error) {
        throw new Error(response.error.message);
      }

      if (response.data.success) {
        toast.success('Certificado emitido com sucesso!');
        fetchProgress();
        
        // Get user profile for preview
        const { data: profile } = await supabase
          .from('profiles')
          .select('full_name')
          .eq('id', user.id)
          .single();

        const cert = response.data.certificate;
        const req = progress.find(p => p.category === category);
        
        setSelectedCertificate({
          certificate: cert,
          userName: profile?.full_name || 'Usuário',
          title: cert.certificate_title || req?.title || category,
          hours: cert.estimated_hours || req?.estimatedHours || 20,
        });
      }
    } catch (error: any) {
      console.error('Error generating certificate:', error);
      toast.error(error.message || 'Erro ao gerar certificado');
    } finally {
      setGenerating(null);
    }
  };

  const handleViewCertificate = async (cert: UserCertificate) => {
    if (!user?.id) return;
    
    const { data: profile } = await supabase
      .from('profiles')
      .select('full_name')
      .eq('id', user.id)
      .single();

    const req = progress.find(p => p.category === cert.category);
    
    setSelectedCertificate({
      certificate: cert,
      userName: profile?.full_name || 'Usuário',
      title: req?.title || categoryLabels[cert.category] || cert.category,
      hours: req?.estimatedHours || 20,
    });
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[1, 2, 3, 4, 5, 6].map(i => (
          <Card key={i}>
            <CardHeader>
              <Skeleton className="h-6 w-32" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-4 w-full mb-2" />
              <Skeleton className="h-4 w-3/4" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const issuedCertificates = progress.filter(p => p.isIssued);
  const availableCertificates = progress.filter(p => !p.isIssued);

  return (
    <div className="space-y-6">
      {selectedCertificate && (
        <CertificatePreview
          certificate={selectedCertificate.certificate}
          userName={selectedCertificate.userName}
          certificateTitle={selectedCertificate.title}
          estimatedHours={selectedCertificate.hours}
          onClose={() => setSelectedCertificate(null)}
        />
      )}

      {issuedCertificates.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Award className="h-5 w-5 text-primary" />
            Meus Certificados ({issuedCertificates.length})
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {issuedCertificates.map(item => (
              <Card key={item.category} className="border-primary/30 bg-primary/5">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">{item.title}</CardTitle>
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="text-sm text-muted-foreground">
                    <p>Código: <span className="font-mono">{item.certificate?.certificate_number}</span></p>
                    <p>Emitido em: {new Date(item.certificate?.issued_at || '').toLocaleDateString('pt-BR')}</p>
                    <p>Acurácia: {item.certificate?.accuracy_percentage}%</p>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => handleViewCertificate(item.certificate!)}
                    >
                      <ExternalLink className="h-4 w-4 mr-1" />
                      Visualizar
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-4">
        <h3 className="text-lg font-semibold">
          Certificados Disponíveis ({availableCertificates.length})
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {availableCertificates.map(item => {
            const questionsProgress = Math.min((item.totalAttempts / item.requiredQuestions) * 100, 100);
            const accuracyMet = item.accuracy >= item.requiredAccuracy;
            
            return (
              <Card key={item.category} className={item.isEligible ? 'border-green-500/50' : ''}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">{item.title}</CardTitle>
                    {item.isEligible ? (
                      <Badge variant="default" className="bg-green-500">Elegível</Badge>
                    ) : (
                      <Lock className="h-4 w-4 text-muted-foreground" />
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">{item.estimatedHours}h estimadas</p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Questões</span>
                      <span className={item.totalAttempts >= item.requiredQuestions ? 'text-green-500' : ''}>
                        {item.totalAttempts}/{item.requiredQuestions}
                      </span>
                    </div>
                    <Progress value={questionsProgress} className="h-2" />
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Acurácia mínima</span>
                      <span className={accuracyMet ? 'text-green-500' : 'text-orange-500'}>
                        {item.accuracy.toFixed(0)}% / {item.requiredAccuracy}%
                      </span>
                    </div>
                    <Progress 
                      value={Math.min((item.accuracy / item.requiredAccuracy) * 100, 100)} 
                      className="h-2" 
                    />
                  </div>

                  <Button 
                    className="w-full" 
                    disabled={!item.isEligible || generating === item.category}
                    onClick={() => handleGenerateCertificate(item.category)}
                  >
                    {generating === item.category ? (
                      'Gerando...'
                    ) : item.isEligible ? (
                      <>
                        <Award className="h-4 w-4 mr-2" />
                        Emitir Certificado
                      </>
                    ) : (
                      'Complete os requisitos'
                    )}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
}
